import React, { useMemo } from 'react';
import { AlertTriangle, TrendingUp, Calendar, Coffee, DollarSign, Briefcase, ShoppingCart } from 'lucide-react';
import { calculatePayment, calculateDaysToRenewal } from '../utils/calculations';
import ActionPlanGenerator from './ActionPlanGenerator';

const PaymentShockCalculator = ({ mortgage, marketRates, user }) => {
  const shockData = useMemo(() => {
    if (!mortgage.current_balance || !mortgage.current_rate || !mortgage.amortization_remain || !mortgage.renewal_date) {
      return null;
    }
    
    // Get best market rate
    const bestMarketRate = marketRates.rates && marketRates.rates.length > 0
      ? Math.min(...marketRates.rates.map(r => r.fixed_5y))
      : mortgage.current_rate;
    
    // Calculate current payment
    const currentPayment = calculatePayment(
      mortgage.current_balance,
      mortgage.current_rate,
      mortgage.amortization_remain,
      mortgage.payment_frequency || 'monthly'
    );
    
    // Calculate renewal payment at market rate
    const renewalPayment = calculatePayment(
      mortgage.current_balance,
      bestMarketRate,
      mortgage.amortization_remain,
      mortgage.payment_frequency || 'monthly'
    );
    
    // Calculate increases
    const monthlyIncrease = renewalPayment.monthlyEquivalent - currentPayment.monthlyEquivalent;
    const annualIncrease = monthlyIncrease * 12;
    const weeklyIncrease = monthlyIncrease / 4.33; // Average weeks per month
    const dailyIncrease = monthlyIncrease / 30.42; // Average days per month
    
    // Calculate percentage increase
    const percentIncrease = ((renewalPayment.monthlyEquivalent - currentPayment.monthlyEquivalent) / currentPayment.monthlyEquivalent) * 100;
    
    // Calculate days to renewal
    const daysToRenewal = calculateDaysToRenewal(mortgage.renewal_date);
    
    // Determine severity
    const severity = percentIncrease > 50 ? 'severe' : percentIncrease > 25 ? 'high' : percentIncrease > 10 ? 'moderate' : 'low';
    
    // Only show if there's an increase
    if (monthlyIncrease <= 0) {
      return null; // No payment shock - rates are lower or same
    }
    
    return {
      currentMonthly: currentPayment.monthlyEquivalent,
      renewalMonthly: renewalPayment.monthlyEquivalent,
      monthlyIncrease,
      annualIncrease,
      weeklyIncrease,
      dailyIncrease,
      percentIncrease,
      currentRate: mortgage.current_rate,
      renewalRate: bestMarketRate,
      rateIncrease: bestMarketRate - mortgage.current_rate,
      daysToRenewal,
      severity
    };
  }, [mortgage, marketRates]);
  
  if (!shockData) {
    return null; // Don't show if no payment shock or incomplete data
  }
  
  const getSeverityColor = () => {
    switch (shockData.severity) {
      case 'severe': return 'border-red-500 bg-red-500/10';
      case 'high': return 'border-orange-500 bg-orange-500/10';
      case 'moderate': return 'border-yellow-500 bg-yellow-500/10';
      default: return 'border-primary bg-primary/10';
    }
  };
  
  const getSeverityText = () => {
    switch (shockData.severity) {
      case 'severe': return 'Critical Payment Increase';
      case 'high': return 'Significant Payment Increase';
      case 'moderate': return 'Notable Payment Increase';
      default: return 'Payment Increase Expected';
    }
  };
  
  return (
    <div className={`border-2 rounded-xl p-6 ${getSeverityColor()}`}>
      <div className="flex items-start gap-4 mb-6">
        <div className="h-12 w-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="h-6 w-6 text-red-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-heading font-semibold mb-1 flex items-center gap-2">
            Payment Shock Calculator
            <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded-full">
              {shockData.daysToRenewal} days to renewal
            </span>
          </h3>
          <p className="text-sm text-muted-foreground">
            Your mortgage payment is expected to increase significantly at renewal based on current market rates
          </p>
        </div>
      </div>
      
      {/* Current vs Renewal Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-background/50 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Current Payment</p>
          <p className="text-3xl font-bold font-mono mb-2">
            ${shockData.currentMonthly.toLocaleString('en-CA', {maximumFractionDigits: 2})}
          </p>
          <p className="text-xs text-muted-foreground">at {shockData.currentRate}% rate</p>
        </div>
        
        <div className="bg-background/50 rounded-lg p-4 border-2 border-red-500/30">
          <p className="text-sm text-muted-foreground mb-1">Expected Renewal Payment</p>
          <p className="text-3xl font-bold font-mono mb-2 text-red-400">
            ${shockData.renewalMonthly.toLocaleString('en-CA', {maximumFractionDigits: 2})}
          </p>
          <p className="text-xs text-muted-foreground">at {shockData.renewalRate}% market rate</p>
        </div>
      </div>
      
      {/* Payment Increase Breakdown */}
      <div className="bg-background/50 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold">{getSeverityText()}</h4>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-red-400" />
            <span className="text-2xl font-bold text-red-400">
              +{shockData.percentIncrease.toFixed(1)}%
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Per Month</p>
            </div>
            <p className="text-xl font-bold font-mono text-red-400">
              +${shockData.monthlyIncrease.toLocaleString('en-CA', {maximumFractionDigits: 0})}
            </p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Per Week</p>
            </div>
            <p className="text-xl font-bold font-mono text-red-400">
              +${shockData.weeklyIncrease.toLocaleString('en-CA', {maximumFractionDigits: 0})}
            </p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Per Day</p>
            </div>
            <p className="text-xl font-bold font-mono text-red-400">
              +${shockData.dailyIncrease.toLocaleString('en-CA', {maximumFractionDigits: 0})}
            </p>
          </div>
          
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Per Year</p>
            </div>
            <p className="text-xl font-bold font-mono text-red-400">
              +${shockData.annualIncrease.toLocaleString('en-CA', {maximumFractionDigits: 0})}
            </p>
          </div>
        </div>
      </div>
      
      {/* Real-Life Impact */}
      <div className="bg-background/50 rounded-lg p-6 mb-6">
        <h4 className="text-sm font-semibold mb-4">What This Increase Means:</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <Coffee className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Daily Budget</p>
              <p className="text-muted-foreground">
                ~${shockData.dailyIncrease.toFixed(0)} extra per day
                <br />
                <span className="text-xs">(≈ {Math.round(shockData.dailyIncrease / 5)} coffees/day)</span>
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <ShoppingCart className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Grocery Impact</p>
              <p className="text-muted-foreground">
                ~${shockData.monthlyIncrease.toFixed(0)} less for other expenses monthly
                <br />
                <span className="text-xs">(≈ {Math.round(shockData.monthlyIncrease / 150)} grocery shops)</span>
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <Briefcase className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Annual Impact</p>
              <p className="text-muted-foreground">
                ${shockData.annualIncrease.toLocaleString('en-CA', {maximumFractionDigits: 0})} more per year
                <br />
                <span className="text-xs">(≈ {Math.round(shockData.annualIncrease / 40000 * 100)}% of median income)</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Items */}
      <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-primary" />
          What You Can Do Now:
        </h4>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">1.</span>
            <span><strong>Start budgeting now</strong> - Adjust spending to prepare for the increase over the next {shockData.daysToRenewal} days</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">2.</span>
            <span><strong>Shop for better rates</strong> - Even 0.25% lower can save you ${((shockData.monthlyIncrease * 0.05).toFixed(0))}/month</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">3.</span>
            <span><strong>Negotiate with your lender</strong> - They may offer retention rates to keep your business</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">4.</span>
            <span><strong>Consider extending amortization</strong> - Spread payments over more years to reduce monthly amount</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-0.5">5.</span>
            <span><strong>Explore HELOC options</strong> - May offer lower rates on a portion of your mortgage</span>
          </li>
        </ul>
      </div>
      
      {/* Urgency Indicator */}
      {shockData.daysToRenewal <= 90 && (
        <div className="mt-4 flex items-center gap-2 text-sm text-red-400">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-medium">
            Time is running out! Only {shockData.daysToRenewal} days until renewal. Take action now to minimize the impact.
          </span>
        </div>
      )}
      
      {/* Action Plan Generator */}
      <ActionPlanGenerator 
        mortgage={mortgage} 
        marketRates={marketRates} 
        user={user}
        shockData={shockData}
      />
    </div>
  );
};

export default PaymentShockCalculator;
